import { Dispatch, memo, SetStateAction, useState, useMemo } from "react";
import Accordion from "../accordian";
import { useTranslation } from "react-i18next";
import CustomSwitch from "../inputs/SwitchInput";
import { useGetSafetyChecklist } from "../../hooks/permit/query";
import Loader from "../loader";

interface SafetyRequirementChecklistCardProps {
  safetyRequirementsChecklist: Record<string, boolean>;
  setSafetyRequirementsChecklist: Dispatch<
    SetStateAction<Record<string, boolean>>
  >;
  isFieldsDisabled: boolean;
}

const SafetyRequirementChecklistCard = ({
  safetyRequirementsChecklist,
  setSafetyRequirementsChecklist,
  isFieldsDisabled,
}: SafetyRequirementChecklistCardProps) => {
  const { t, i18n } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { data: safetyChecklist } = useGetSafetyChecklist();

  const safetyRequirementsChecklistOptions = useMemo(() => {
    if (!safetyChecklist?.categories || !safetyChecklist?.items) return [];

    return safetyChecklist.categories
      .sort((a: any, b: any) => a.n_DisplayOrder - b.n_DisplayOrder)
      .map((category: any) => ({
        id: category.n_CategoryID.toString(),
        titleEn: category.s_CategoryName_En,
        titleAr: category.s_CategoryName_Ar,
        items: safetyChecklist.items
          .filter((item: any) => item.n_CategoryID === category.n_CategoryID)
          .sort((a: any, b: any) => a.n_DisplayOrder - b.n_DisplayOrder)
          .map((item: any) => ({
            id: item.n_ItemID.toString(),
            titleEn: item.s_ItemText_En,
            titleAr: item.s_ItemText_Ar || item.s_ItemText_En, // Fallback to English if Arabic is null
          })),
      }));
  }, [safetyChecklist]);

  const handleToggle = (id: string, value: boolean) => {
    setSafetyRequirementsChecklist((prev: Record<string, boolean>) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <div className="bg-white z-10 p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-start text-xl sm:text-2xl flex flex-1 -mt-2 items-center">
            {t("3")}. {t("safetyRequirementChecklist")}
          </h1>
        }
      >
        <div className="flex flex-col gap-6">
          {safetyRequirementsChecklistOptions.length === 0 ? (
            <Loader size={18} />
          ) : (
            safetyRequirementsChecklistOptions.map((requirement: any) => {
              return (
                <div key={requirement.id} className="flex flex-col gap-3">
                  <p className="font-semibold">
                    {i18n.language === "en"
                      ? requirement.titleEn
                      : requirement.titleAr}
                  </p>
                  <div className="grid md:grid-cols-2 gap-x-2 lg:gap-x-4 gap-y-3">
                    {requirement.items.map((item: any) => (
                      <div
                        key={item.id}
                        className="border-b border-gray-200 pb-2 last:border-0 md:last:border-b"
                      >
                        <CustomSwitch
                          checked={!!safetyRequirementsChecklist[item.id]}
                          onChange={(checked: any) =>
                            handleToggle(item.id, checked)
                          }
                          label={
                            i18n.language === "en" ? item.titleEn : item.titleAr
                          }
                          id={item.id}
                          disabled={isFieldsDisabled}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Accordion>
    </div>
  );
};

export default memo(SafetyRequirementChecklistCard);
